import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon, Modal, Notice } from '../exchanges/components';
import { fold, formatDateTime } from './api';
import SearchSelect from './SearchSelect';
import { FORM_INTERFACES, hasPermission } from '../shared/adminInterfaces';
import ExchangeTemplateEditor from './ExchangeTemplateEditor';
import ExchangeIntroductionEditor from './ExchangeIntroductionEditor';
const { mappingTargetKey, wishFieldInfo } = require('../shared/candidatureVoeux');

const newId = () => `l-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const WISH_HELP = 'Champ propre à chaque vœu : la valeur est écrite dans le vœu de la filière envoyée (1, 2 ou 3).';

const fieldTypeLabel = (field) => {
  const types = { string: 'Texte', textarea: 'Texte long', picklist: 'Liste', multipicklist: 'Liste multiple', boolean: 'Case à cocher', date: 'Date', datetime: 'Date et heure', email: 'E-mail', phone: 'Téléphone', double: 'Nombre', int: 'Entier', currency: 'Montant', percent: 'Pourcentage', reference: 'Référence', url: 'URL' };
  const base = types[field.type] || field.type;
  return `${base}${field.length && ['string', 'textarea', 'email', 'phone', 'url'].includes(field.type) ? ` (${field.length})` : ''}${field.restricted ? ' · liste restreinte' : ''}`;
};

// Valeur transmise proposée d'office selon la question choisie.
function suggestTransform(question) {
  if (!question) return 'identity';
  if (question.type === 'search') return 'searchLabel';
  if (/dipl[oô]me/i.test(question.label)) return 'diploma';
  return 'identity';
}

export default function SalesforceMapping({ api, permissions }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const allowedProfiles = FORM_INTERFACES.filter(item => hasPermission(permissions, item.key)).map(item => item.key.split(':')[1]);
  const requestedProfile = searchParams.get('profil');
  const profileKey = allowedProfiles.includes(requestedProfile) ? requestedProfile : allowedProfiles.includes('candidature') ? 'candidature' : allowedProfiles[0];
  const [profiles, setProfiles] = useState([]);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [issues, setIssues] = useState({ errors: [], warnings: [] });
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [confirmDefault, setConfirmDefault] = useState(false);
  const [templateDirty, setTemplateDirty] = useState(false);
  const [exchangeTab, setExchangeTab] = useState('template');
  const templateSaved = useCallback(() => {
    api('/salesforce/profiles/echange').then(data => {
      if (detail?.profile.key !== 'echange') return;
      if (same(draft, detail.mapping.lines)) {
        setDetail(data); setDraft(data.mapping.lines);
      } else {
        setDetail(previous => previous?.profile.key === 'echange' ? { ...previous, questions: data.questions } : previous);
        setMessage('Le modèle a été enregistré. Les correspondances ont été renumérotées ; rechargez-les avant de reprendre vos modifications non enregistrées.');
      }
    }).catch(e => setError(e.message));
  }, [api, detail, draft]);

  const dirty = detail ? !same(draft, detail.mapping.lines) : false;

  const refreshProfiles = useCallback(() => api('/salesforce/profiles').then((data) => setProfiles(data.profiles)), [api]);
  useEffect(() => { refreshProfiles().catch((e) => setError(e.message)); }, [refreshProfiles]);
  useEffect(() => {
    if (profileKey === 'echange') return undefined;
    let active = true;
    api('/salesforce/objects').then(data => { if (active) setCatalog(data); }).catch(e => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [api, profileKey]);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setMessage(''); setIssues({ errors: [], warnings: [] });
    api(`/salesforce/profiles/${encodeURIComponent(profileKey)}`).then((data) => {
      if (!active) return;
      setDetail(data); setDraft(data.mapping.lines);
    }).catch((e) => { if (active) { setDetail(null); setError(e.message); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, profileKey]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const requested = useRef(new Set());
  const loadFields = useCallback((sobject) => {
    if (!sobject || requested.current.has(sobject)) return;
    requested.current.add(sobject);
    setFields((p) => ({ ...p, [sobject]: { status: 'loading' } }));
    api(`/salesforce/objects/${encodeURIComponent(sobject)}/fields`)
      .then((data) => setFields((p) => ({ ...p, [sobject]: { status: 'ready', data } })))
      .catch((e) => setFields((p) => ({ ...p, [sobject]: { status: 'error', error: e.message } })));
  }, [api]);

  // Libellés des objets utilisés dans la correspondance affichée.
  useEffect(() => { if (profileKey !== 'echange') [...new Set(draft.map((line) => line.sobject))].forEach(loadFields); }, [draft, loadFields, profileKey]);

  const questions = useMemo(() => detail?.questions || [], [detail]);
  const questionById = useMemo(() => new Map(questions.map((q, index) => [String(q.questionId), { ...q, index }])), [questions]);
  const objectLabel = useCallback((name) => catalog?.objects.find((o) => o.name === name)?.label || name, [catalog]);
  const transformLabel = (key) => detail?.transforms.find((t) => t.key === key)?.label || key;

  function sourceView(line) {
    const { source } = line;
    if (source.type === 'question') {
      const question = questionById.get(String(source.questionId));
      return { badge: `Q ${source.questionId}`, label: question?.label || line.sourceLabel || 'Question', detail: question ? question.section : 'Question absente du modèle actuel', missing: !question };
    }
    if (source.type === 'dossier') {
      const item = detail?.dossierSources.find((s) => s.key === source.key);
      return { badge: 'Dossier', label: item?.label || source.key, detail: 'Donnée du dossier' };
    }
    return { badge: 'Fixe', label: `« ${source.value} »`, detail: 'Valeur fixe' };
  }

  const order = (line) => {
    if (line.source.type === 'dossier') return -2;
    if (line.source.type === 'constant') return -1;
    const question = questionById.get(String(line.source.questionId));
    return question ? question.index : 1e6;
  };

  const rows = useMemo(() => {
    const words = fold(search).split(/\s+/).filter(Boolean);
    return draft
      .map((line, position) => ({ line, position }))
      .filter(({ line }) => {
        if (!words.length) return true;
        const view = sourceView(line);
        const haystack = fold(`${view.badge} ${view.label} ${view.detail} ${line.sobject} ${objectLabel(line.sobject)} ${line.field} ${line.fieldLabel} ${transformLabel(line.transform)}`);
        return words.every((word) => haystack.includes(word));
      })
      .sort((a, b) => order(a.line) - order(b.line) || a.position - b.position);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, search, questionById, catalog, detail]);

  const unsent = useMemo(() => {
    const mapped = new Set(draft.filter((l) => l.enabled !== false && l.source.type === 'question').map((l) => String(l.source.questionId)));
    return questions.filter((q) => q.mappable && !mapped.has(String(q.questionId)));
  }, [draft, questions]);
  const missing = draft.filter((l) => l.source.type === 'question' && !questionById.has(String(l.source.questionId)));

  const issuesFor = (lineId) => ({
    errors: issues.errors.filter((i) => i.lineId === lineId),
    warnings: issues.warnings.filter((i) => i.lineId === lineId)
  });

  function selectProfile(key) {
    if (key === profileKey) return;
    if ((dirty || templateDirty) && !window.confirm('Les modifications non enregistrées de ce profil seront perdues. Continuer ?')) return;
    setSearchParams((previous) => { const next = new URLSearchParams(previous); next.set('profil', key); return next; });
  }

  function selectExchangeTab(tab) {
    if (tab === exchangeTab) return;
    if (templateDirty && !window.confirm('Les modifications non enregistrées seront perdues. Continuer ?')) return;
    setExchangeTab(tab);
  }

  function applyLine(line) {
    setDraft((previous) => previous.some((l) => l.id === line.id) ? previous.map((l) => (l.id === line.id ? line : l)) : [...previous, line]);
    setIssues((previous) => ({ errors: previous.errors.filter((i) => i.lineId !== line.id), warnings: previous.warnings.filter((i) => i.lineId !== line.id) }));
    setEditing(null); setMessage('');
  }

  function blankLine(question) {
    return {
      id: newId(),
      enabled: true,
      source: question ? { type: 'question', questionId: question.questionId } : { type: 'question', questionId: null },
      sourceLabel: question?.label || '',
      transform: suggestTransform(question),
      sobject: detail.profile.objects?.[0] || 'Case',
      field: '',
      fieldLabel: ''
    };
  }

  async function loadDefault() {
    setBusy(true); setError(''); setConfirmDefault(false);
    try {
      const data = await api(`/salesforce/profiles/${encodeURIComponent(profileKey)}/default`);
      setDraft(data.lines); setIssues({ errors: [], warnings: [] });
      setMessage(data.lines.length ? 'Correspondance par défaut chargée. Vérifiez-la puis enregistrez-la.' : 'Ce profil n’a pas de correspondance par défaut : la liste est vide.');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function save() {
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await api(`/salesforce/profiles/${encodeURIComponent(profileKey)}`, { method: 'PUT', body: { lines: draft, expectedRevision: detail.mapping.revision } });
      setDetail((previous) => ({ ...previous, mapping: result.mapping, diagnostics: result.diagnostics }));
      setDraft(result.mapping.lines);
      setIssues({ errors: [], warnings: result.warnings || [] });
      setMessage(`Correspondance enregistrée (révision ${result.mapping.revision}).${detail.profile.wired ? ' Elle s’applique dès le prochain envoi.' : ''}`);
      refreshProfiles().catch(() => {});
    } catch (e) {
      setError(e.message);
      if (e.details) setIssues({ errors: e.details.errors || [], warnings: e.details.warnings || [] });
    } finally { setBusy(false); }
  }

  const profile = detail?.profile;
  const mapping = detail?.mapping;

  return <>
    <div className="ex-page-heading"><div><p className="ex-eyebrow">ADMINISTRATION DES INSCRIPTIONS</p><h1>Formulaires et correspondances</h1><p className="ex-muted">Gérez le modèle des étudiants en échange et les correspondances Salesforce.</p></div>
      <div className="ex-actions" style={profileKey === 'echange' ? { display: 'none' } : undefined}>
        {dirty && <button className="ex-button" disabled={busy} onClick={() => { setDraft(mapping.lines); setIssues({ errors: [], warnings: [] }); setMessage(''); }}>Annuler les modifications</button>}
        <button className="ex-button ex-button--primary" disabled={busy || !dirty || loading} onClick={save}><Icon name="check" size={17} />{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
      </div>
    </div>

    <div className="ad-profile-tabs" role="tablist" aria-label="Profil">
      {(profiles.length ? profiles : [{ key: profileKey, label: '…' }]).map((item) => <button key={item.key} role="tab" aria-selected={item.key === profileKey} className={item.key === profileKey ? 'is-active' : ''} onClick={() => selectProfile(item.key)}>
        <strong>{item.label}</strong>
        {item.key !== 'echange' && item.wired !== undefined && <span className={`ad-badge ${item.wired ? 'ad-badge--ok' : 'ad-badge--muted'}`}>{item.wired ? 'Câblé à l’envoi' : 'Non câblé'}</span>}
        {item.key !== 'echange' && item.count !== undefined && <small>{item.count} correspondance(s){item.isDefault ? ' · par défaut' : ''}</small>}
      </button>)}
    </div>

    <Notice>{error}</Notice><Notice type="success">{message}</Notice>

    {loading ? <div className="ex-empty" role="status"><div className="ex-feature-icon"><Icon name="link" size={28} /></div><h3>{profileKey === 'echange' ? 'Chargement du formulaire…' : 'Chargement de la correspondance…'}</h3></div>
      : !detail ? null : <>
        {profile.key === 'echange' && <>
          <div className="ad-profile-tabs" role="tablist" aria-label="Réglages du formulaire en échange"><button role="tab" aria-selected={exchangeTab === 'template'} className={exchangeTab === 'template' ? 'is-active' : ''} onClick={() => selectExchangeTab('template')}>Questions du modèle</button><button role="tab" aria-selected={exchangeTab === 'introduction'} className={exchangeTab === 'introduction' ? 'is-active' : ''} onClick={() => selectExchangeTab('introduction')}>Message introductif</button></div>
          {exchangeTab === 'template' ? <ExchangeTemplateEditor api={api} onDirty={setTemplateDirty} onSaved={templateSaved} /> : <ExchangeIntroductionEditor api={api} onDirty={setTemplateDirty} />}
        </>}
        {profile.key !== 'echange' && <div>
        {!profile.wired && <Notice type="info">Le profil « {profile.label} » n’est pas encore câblé : ses envois restent sur Web-to-Case. Cette correspondance peut être préparée ici, elle n’a pour l’instant aucun effet.</Notice>}
        {profile.key === 'candidature' && <Notice type="info">Contact, Candidature en test et Candidature mettent à jour des enregistrements existants. Le Contact est retrouvé par son adresse e-mail personnelle (hed__AlternateEmail__c), Candidature en test par le token du dossier (Token__c), et Candidature par le lien Inscription__c de cette dernière. Une cible absente ou ambiguë bloque l’envoi. L’envoi modifie uniquement les champs configurés ; une réponse visible vide efface la valeur dans Salesforce. Aucune Demande n’est créée. Chaque filière s’envoie séparément : les champs propres à un vœu (Niveau, Filière, Filière objet, Parcours, Admission, Avis du responsable, Commentaire) sont écrits dans le vœu 1, 2 ou 3 de la filière envoyée, quel que soit le numéro choisi ici.</Notice>}
        <div className="ad-status-bar">
          <span><Icon name="folder" size={16} />Modèle de formulaire : <strong>{profile.template}</strong> ({questions.length} questions)</span>
          <span><Icon name="refresh" size={16} />{mapping.isDefault ? 'Aucune correspondance enregistrée' : `Révision ${mapping.revision} · enregistrée le ${formatDateTime(mapping.updatedAt)}${mapping.updatedBy ? ` par ${mapping.updatedBy}` : ''}`}</span>
          {catalog && <span className={catalog.source === 'live' ? '' : 'ad-status-warn'}><Icon name="link" size={16} />Champs Salesforce : {catalog.source === 'live' ? 'lus en direct' : `instantané du ${catalog.generatedAt}${catalog.reason ? ` (${catalog.reason})` : ''}`}</span>}
          {dirty && <span className="ex-unsaved">Modifications non enregistrées</span>}
        </div>

        <section className="ex-list-card">
          <div className="ex-list-toolbar">
            <label className="ex-search"><Icon name="search" size={19} /><span className="ex-sr-only">Rechercher une correspondance</span><input type="search" placeholder="Rechercher une question, un champ, un nom d’API…" value={search} onChange={(e) => setSearch(e.target.value)} /></label>
            <div className="ex-actions">
              <button className="ex-button" disabled={busy} onClick={() => (draft.length ? setConfirmDefault(true) : loadDefault())}><Icon name="refresh" size={16} />Correspondance par défaut</button>
              <button className="ex-button ex-button--primary" disabled={busy} onClick={() => setEditing(blankLine(null))}><Icon name="plus" size={17} />Ajouter une correspondance</button>
            </div>
          </div>
          {confirmDefault && <div className="ex-remove-confirm ad-inline-confirm"><p>Remplacer la liste affichée par la correspondance par défaut ? Rien n’est enregistré tant que vous ne cliquez pas sur « Enregistrer ».</p><div className="ex-actions"><button className="ex-button" onClick={() => setConfirmDefault(false)}>Annuler</button><button className="ex-button ex-button--primary" onClick={loadDefault}>Charger</button></div></div>}
          {!draft.length ? <div className="ex-empty"><div className="ex-feature-icon"><Icon name="link" size={30} /></div><h3>Aucune correspondance</h3><p>Ajoutez une première correspondance entre une donnée du formulaire et un champ Salesforce.</p></div>
            : <div className="ex-table-scroll"><table className="ad-mapping-table">
              <caption className="ex-sr-only">Correspondances du profil {profile.label}</caption>
              <thead><tr><th scope="col">Donnée de la candidature</th><th scope="col">Valeur transmise</th><th scope="col">Objet Salesforce</th><th scope="col">Champ Salesforce</th><th scope="col">Actif</th><th scope="col" className="ex-dossier-action-heading">Actions</th></tr></thead>
              <tbody>{rows.map(({ line }) => {
                const view = sourceView(line);
                const lineIssues = issuesFor(line.id);
                const objectFields = fields[line.sobject]?.data?.fields;
                const fieldLabel = objectFields?.find((f) => f.name === line.field)?.label || line.fieldLabel;
                return <React.Fragment key={line.id}>
                  <tr className={`${line.enabled === false ? 'is-disabled' : ''} ${lineIssues.errors.length ? 'has-error' : ''}`}>
                    <td><div className="ad-cell-source"><span className={`ad-code ${view.missing ? 'ad-code--error' : ''}`}>{view.badge}</span><span><strong>{view.label}</strong><small>{view.detail}</small></span></div></td>
                    <td>{transformLabel(line.transform)}</td>
                    <td><span className="ad-cell-api"><strong>{objectLabel(line.sobject)}</strong><code>{line.sobject}</code></span></td>
                    <td><span className="ad-cell-api"><strong>{fieldLabel || '—'}</strong><code>{line.field}</code>{wishFieldInfo(line.sobject, line.field) && <small title={WISH_HELP}>Selon le vœu envoyé (1, 2 ou 3)</small>}</span></td>
                    <td><input type="checkbox" checked={line.enabled !== false} aria-label="Correspondance active" onChange={(e) => setDraft((previous) => previous.map((l) => (l.id === line.id ? { ...l, enabled: e.target.checked } : l)))} /></td>
                    <td className="ex-dossier-action-cell"><div className="ex-row-actions">
                      <button className="ex-icon-button" title="Modifier" aria-label="Modifier la correspondance" onClick={() => setEditing(line)}><Icon name="edit" size={17} /></button>
                      <button className="ex-icon-button" title="Supprimer" aria-label="Supprimer la correspondance" onClick={() => setDraft((previous) => previous.filter((l) => l.id !== line.id))}><Icon name="trash" size={17} /></button>
                    </div></td>
                  </tr>
                  {(lineIssues.errors.length > 0 || lineIssues.warnings.length > 0) && <tr className="ad-issue-row"><td colSpan={6}>
                    {lineIssues.errors.map((i, n) => <p key={`e${n}`} className="ad-issue ad-issue--error"><Icon name="alert" size={15} />{i.message}</p>)}
                    {lineIssues.warnings.map((i, n) => <p key={`w${n}`} className="ad-issue ad-issue--warn"><Icon name="alert" size={15} />{i.message}</p>)}
                  </td></tr>}
                </React.Fragment>;
              })}</tbody>
            </table></div>}
          <div className="ex-list-footer"><span>{draft.filter((l) => l.enabled !== false).length} correspondance(s) active(s) sur {draft.length}{rows.length !== draft.length ? ` · ${rows.length} affichée(s)` : ''}</span></div>
        </section>

        {missing.length > 0 && <Notice>{missing.length} correspondance(s) visent une question absente du modèle actuel : modifiez-les ou supprimez-les avant d’enregistrer.</Notice>}

        <details className="ad-panel" open={profile.wired && unsent.length > 0 && unsent.length <= 15}>
          <summary>Questions du modèle non transmises ({unsent.length})</summary>
          <p className="ex-help">Ces questions ne sont envoyées vers aucun champ Salesforce. C’est normal pour certaines ; les autres peuvent être associées ici.</p>
          {unsent.length ? <ul className="ad-unsent-list">{unsent.map((q) => <li key={q.questionId}>
            <span className="ad-code">Q {q.questionId}</span>
            <span className="ad-option-main">{q.label}<small>{q.section}{q.legacyIdForm ? ` · identifiant Web-to-Case ${q.legacyIdForm}` : ''}</small></span>
            <button className="ex-button ex-button--quiet" onClick={() => setEditing(blankLine(q))}><Icon name="plus" size={15} />Associer</button>
          </li>)}</ul> : <p className="ex-empty-small">Toutes les questions du modèle sont transmises.</p>}
        </details>
        </div>}
      </>}

    {editing && detail && <MappingEditor
      line={editing}
      detail={detail}
      catalog={catalog}
      fields={fields}
      loadFields={loadFields}
      usedTargets={new Set(draft.filter((l) => l.enabled !== false && l.id !== editing.id).map((l) => mappingTargetKey(l.sobject, l.field)))}
      isNew={!draft.some((l) => l.id === editing.id)}
      onClose={() => setEditing(null)}
      onApply={applyLine}
    />}
  </>;
}

function MappingEditor({ line, detail, catalog, fields, loadFields, usedTargets, isNew, onClose, onApply }) {
  const { profile, questions, dossierSources, transforms } = detail;
  const [value, setValue] = useState(line);
  const [problem, setProblem] = useState('');
  const set = (patch) => { setValue((previous) => ({ ...previous, ...patch })); setProblem(''); };
  useEffect(() => { loadFields(value.sobject); }, [value.sobject, loadFields]);

  const objectState = fields[value.sobject];
  const objectFields = objectState?.data?.fields || [];
  const selectedField = objectFields.find((f) => f.name === value.field);
  const fieldsUnavailable = objectState?.status === 'error';

  const questionOptions = useMemo(() => questions.map((q) => ({
    value: String(q.questionId), badge: `Q ${q.questionId}`, label: q.label || '(sans étiquette)', detail: `${q.section} · ${q.type}`,
    disabled: !q.mappable, disabledReason: 'Pièce jointe : non transmise au CRM'
  })), [questions]);

  const objectOptions = useMemo(() => {
    const list = (catalog?.objects || []).map((o) => ({
      value: o.name, label: o.label, badge: o.name,
      detail: o.detailed ? undefined : 'Champs disponibles seulement en direct',
      disabled: Boolean(profile.objects && !profile.objects.includes(o.name)),
      disabledReason: 'Non pris en charge par l’envoi actuel de ce profil'
    }));
    if (!list.some((o) => o.value === value.sobject)) list.unshift({ value: value.sobject, label: value.sobject, badge: value.sobject });
    return list.sort((a, b) => Number(a.disabled) - Number(b.disabled));
  }, [catalog, profile, value.sobject]);

  const fieldOptions = useMemo(() => objectFields.map((f) => ({
    value: f.name, label: f.label, badge: f.name, detail: fieldTypeLabel(f),
    disabled: usedTargets.has(mappingTargetKey(value.sobject, f.name)), disabledReason: 'Déjà alimenté par une autre correspondance active'
  })), [objectFields, usedTargets, value.sobject]);

  const transform = transforms.find((t) => t.key === value.transform);

  function chooseQuestion(id) {
    const question = questions.find((q) => String(q.questionId) === id);
    set({ source: { type: 'question', questionId: question.questionId }, sourceLabel: question.label, transform: suggestTransform(question) });
  }

  function changeSourceType(type) {
    if (type === value.source.type) return;
    if (type === 'question') set({ source: { type, questionId: null }, sourceLabel: '', transform: 'identity' });
    if (type === 'dossier') set({ source: { type, key: dossierSources[0].key }, sourceLabel: dossierSources[0].label, transform: dossierSources[0].transform });
    if (type === 'constant') set({ source: { type, value: '' }, sourceLabel: '', transform: 'identity' });
  }

  function submit(event) {
    event.preventDefault();
    const { source } = value;
    if (source.type === 'question' && source.questionId == null) return setProblem('Choisissez la question du formulaire.');
    if (source.type === 'constant' && !String(source.value || '').trim()) return setProblem('Indiquez la valeur fixe.');
    if (!value.sobject) return setProblem('Choisissez l’objet Salesforce.');
    if (!value.field) return setProblem('Choisissez le champ Salesforce.');
    if (value.enabled !== false && usedTargets.has(mappingTargetKey(value.sobject, value.field))) return setProblem('Ce champ est déjà alimenté par une autre correspondance active.');
    onApply({ ...value, fieldLabel: selectedField?.label || value.fieldLabel || '' });
  }

  return <Modal title={isNew ? 'Nouvelle correspondance' : 'Modifier la correspondance'} wide onClose={onClose}>
    <form className="ex-form ad-editor" onSubmit={submit}>
      <Notice>{problem}</Notice>
      <fieldset className="ad-editor-step">
        <legend><span className="ad-step">1</span>Donnée de la candidature</legend>
        <div className="ad-segmented" role="radiogroup" aria-label="Type de donnée">
          {[['question', 'Question du formulaire'], ['dossier', 'Donnée du dossier'], ['constant', 'Valeur fixe']].map(([key, label]) =>
            <button type="button" key={key} role="radio" aria-checked={value.source.type === key} className={value.source.type === key ? 'is-active' : ''} onClick={() => changeSourceType(key)}>{label}</button>)}
        </div>
        {value.source.type === 'question' && <SearchSelect label="Question (numéro et étiquette)" options={questionOptions} value={value.source.questionId == null ? '' : String(value.source.questionId)} onChange={chooseQuestion} placeholder="Numéro, étiquette ou rubrique…" />}
        {value.source.type === 'dossier' && <label>Donnée du dossier<select value={value.source.key} onChange={(e) => { const item = dossierSources.find((s) => s.key === e.target.value); set({ source: { type: 'dossier', key: item.key }, sourceLabel: item.label, transform: item.transform }); }}>
          {dossierSources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>{dossierSources.find((s) => s.key === value.source.key)?.help && <small>{dossierSources.find((s) => s.key === value.source.key).help}</small>}</label>}
        {value.source.type === 'constant' && <label>Valeur fixe<input maxLength={255} value={value.source.value} onChange={(e) => set({ source: { type: 'constant', value: e.target.value } })} placeholder="Par exemple : true, ou une valeur de liste Salesforce" /><small>Transmise telle quelle à chaque envoi.</small></label>}
        <label>Valeur transmise<select value={value.transform} onChange={(e) => set({ transform: e.target.value })}>
          {transforms.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>{transform?.help && <small>{transform.help}</small>}</label>
      </fieldset>

      <fieldset className="ad-editor-step">
        <legend><span className="ad-step">2</span>Destination dans Salesforce</legend>
        <SearchSelect label="Objet Salesforce" options={objectOptions} value={value.sobject} onChange={(name) => set({ sobject: name, field: '', fieldLabel: '' })} placeholder="Libellé ou nom d’API (ex. Contact)…"
          help={profile.objects ? `Objets disponibles : ${profile.objects.join(', ')}. Mise à jour uniquement.` : undefined} />
        {objectState?.status === 'loading' && <p className="ex-help" role="status">Chargement des champs de {value.sobject}…</p>}
        {fieldsUnavailable && <Notice type="info">{objectState.error}{!profile.wired && ' Vous pouvez saisir le nom d’API du champ à la main.'}</Notice>}
        {objectState?.status === 'ready' && <SearchSelect label="Champ Salesforce (étiquette et nom d’API)" options={fieldOptions} value={value.field} onChange={(name, option) => set({ field: name, fieldLabel: option.label })} placeholder="Étiquette ou nom d’API (ex. SuppliedFirstName__c)…" />}
        {fieldsUnavailable && !profile.wired && <div className="ex-identity-fields">
          <label>Nom d’API du champ<input value={value.field} onChange={(e) => set({ field: e.target.value.trim() })} placeholder="Exemple__c" pattern="[A-Za-z][A-Za-z0-9_]*" /></label>
          <label>Étiquette (affichage)<input value={value.fieldLabel} onChange={(e) => set({ fieldLabel: e.target.value })} /></label>
        </div>}
        {objectState?.status === 'ready' && objectState.data?.permissionsVerified === false && <Notice type="info">Les champs proviennent d’un instantané. Les droits de modification seront vérifiés auprès de Salesforce avant l’envoi.</Notice>}
        {selectedField && <div className="ad-field-card">
          <div><span>Étiquette</span><strong>{selectedField.label}</strong></div>
          <div><span>Nom d’API</span><code>{selectedField.name}</code></div>
          <div><span>Type</span><strong>{fieldTypeLabel(selectedField)}</strong></div>
          {selectedField.nillable === false && selectedField.type !== 'boolean' && <div><span>Obligatoire</span><strong>Oui</strong></div>}
          {wishFieldInfo(value.sobject, selectedField.name) && <div><span>Vœu</span><strong>{WISH_HELP}</strong></div>}
          {selectedField.picklist && <details><summary>{selectedField.picklist.length} valeur(s) de liste</summary><ul className="ad-picklist">{selectedField.picklist.slice(0, 80).map((v) => <li key={v}>{v}</li>)}</ul>{selectedField.picklist.length > 80 && <small>… et {selectedField.picklist.length - 80} autres.</small>}</details>}
        </div>}
      </fieldset>

      <label className="ex-checkbox"><input type="checkbox" checked={value.enabled !== false} onChange={(e) => set({ enabled: e.target.checked })} />Correspondance active (transmise à l’envoi)</label>
      <p className="ex-help">La correspondance est ajoutée à la liste ; elle n’est prise en compte qu’après « Enregistrer ».</p>
      <div className="ex-modal-footer">
        <button type="button" className="ex-button" onClick={onClose}>Annuler</button>
        <button className="ex-button ex-button--primary"><Icon name="check" size={16} />Valider</button>
      </div>
    </form>
  </Modal>;
}
